import { createSelector } from '@reduxjs/toolkit';
import { createCachedSelector, LruObjectCache } from 're-reselect';

import { registerCachedSelectors } from 'app-components/utils/core/reselect/selectorCacheRegistry';
import type RootState from 'app-components/utils/redux-store/rootState';

import { selectorsHelpers } from '../core';
import { TAGS_REDUCER_KEY } from './tagsTypes';

type State = Pick<RootState, typeof TAGS_REDUCER_KEY>;

export const getTagsStore = (state: State) => state[TAGS_REDUCER_KEY];

export const getResourcesById = (state: State) => getTagsStore(state).resourcesById;
export const getLoadPending = (state: State) => getTagsStore(state).loadPending;
export const getIsLoading = (state: State) => getTagsStore(state).isLoading;
export const getError = (state: State) => getTagsStore(state).error;
export const getLastSyncDate = (state: State) => getTagsStore(state).lastSyncDate;

export const getTags = getResourcesById;

export const getTagById = createSelector(
  getTags,
  (_state: unknown, id: string) => id,
  (tags, id) => tags?.get(id) || null,
);

export const getTagByName = createSelector(
  getTags,
  (_state: unknown, name: string) => name,
  (tags, name) => tags?.find((x) => x.name === name) || null,
);

export const getTagsByIds = createCachedSelector(
  getTags,
  selectorsHelpers.createValueCache((_state: unknown, ids?: string[]) => ids), // protect from mutable ids array
  (tags, ids) => tags?.filter((tag) => tag.id !== undefined && ids?.includes(tag.id)),
)({ keySelector: (_state, ids) => ids?.join?.(',') || 'NA', cacheObject: new LruObjectCache({ cacheSize: 6 }) });

// A store reset drops every key's selector (see selectorCacheRegistry).
registerCachedSelectors(getTagsByIds);
